import React, { MutableRefObject, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { Tooltip } from '@microservices/wiskey-react-components'
import { GridRenderCellParams } from '@microservices/wiskey-react-table-component'
import { Box, Skeleton } from '@mui/material'

import { PopperContextMenu } from '@components/PopperContextMenu'

import { PrintTrigger } from '@features/print-forms/components/PrintTrigger'

import { useAppSelector, useTooltip } from '@hooks'
import {
  ACTION_CODE,
  EVENT_CODE,
  SELECTED_CELL_STYLE,
  TIME_LINE_CELL_MAX_ITEMS_COUNT,
  TIMELINE_CELL_DATA_ATTR,
} from '@constants'
import { ObjectDataRecord, Position, TimelineCellContext, TimeLineDTOType } from '@types'

import { getCellId, isBottomPinnedTotalRow, objectXCellRenderer } from '../../helpers'
import { useClickHandler, useHandlers, useTimeLineContextMenu } from '../../hooks'
import { PinnedAggregationRowCell } from '../PinnedAggregationRowCell'

import { useHandlers as useObjectXDynamicCellHandlers } from './hooks/useHandlers'

export type ObjectXDynamicCellProps = {
  config: TimeLineDTOType
  handlers: ReturnType<typeof useHandlers>
  params: GridRenderCellParams
  cellContextRef: MutableRefObject<TimelineCellContext>
}

export const ObjectXDynamicCell = (props: ObjectXDynamicCellProps) => {
  const { handlers, config, params, cellContextRef } = props

  const cellId = getCellId(params.row.id, params.field)
  const isCellSelected =
    Boolean(cellContextRef.current.selectedCells[cellId]) &&
    cellContextRef.current.selectedCells[cellId].events.length === params.value?.length

  const {
    data,
    handlers: {
      handleExecuteActions,
      handleChangeSelectedCells,
      handleToggleSelectedEvent,
      handleClearSelectedCells,
      cellItemDrag,
    },
  } = handlers

  const loadingItemIds = useAppSelector(state => state.timeLineGrid.loadingItemIds)

  const { objectXActionsObject, objectXPKField } = data

  const { onClick: onClickWithDelay, onDoubleClick } = useClickHandler({ delay: 250 })

  const {
    handleContextMenu,
    contextMenuData,
    onRefContextMenu,
    onCloseContextMenu,
    position,
    printFormVouPayload,
    onClosePrintFormVou,
  } = useTimeLineContextMenu({
    handleClearSelectedCells,
  })

  const { tooltipPosition, tooltipData, handleOpenTooltip, handleCloseTooltip } = useTooltip()

  const {
    handleCellLeftClick,
    handleCellItemDoubleLeftClick,
    handleCellItemRightClick,
    handleMouseEnter,
    handleMouseDown,
    handleCellItemLeftClick,
    handleCellItemMouseOver,
    handleCellRightClick,
    handleMouseUp,
  } = useObjectXDynamicCellHandlers({
    config,
    params,
    cellContextRef,
    objectXPKField,
    handleContextMenu,
    handleChangeSelectedCells,
    handleToggleSelectedEvent,
    onClickWithDelay,
    onDoubleClick,
    handleExecuteActions,
    objectXActionsObject,
    handleOpenTooltip,
  })

  useEffect(() => {
    return () => {
      handleCloseTooltip()
    }
  }, [])

  if (isBottomPinnedTotalRow(params.row.id)) {
    return <PinnedAggregationRowCell config={config} params={params} />
  }

  return (
    <>
      <Box
        {...{ [TIMELINE_CELL_DATA_ATTR]: cellId }}
        sx={{
          width: '100%',
          height: '100%',
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'column',
          ...(isCellSelected && SELECTED_CELL_STYLE),
        }}
        onClick={handleCellLeftClick}
        onContextMenu={handleCellRightClick}
        onMouseDown={handleMouseDown}
        onMouseLeave={e => cellItemDrag?.onCellMouseLeave(e, cellId)}
        onMouseEnter={e => {
          handleMouseEnter()

          cellItemDrag?.onCellMouseEnter({
            event: e,
            params,
            rowId: params?.row?.id as string,
            columnId: String(params?.field),
            cellId,
          })
        }}
        onMouseUp={() => {
          cellItemDrag?.onCellMouseUp()
          handleMouseUp()
        }}
      >
        <PopperContextMenu
          dataTransfer={contextMenuData.dataTransfer}
          items={contextMenuData.items}
          position={position}
          onClose={onCloseContextMenu}
          onItemClick={contextMenuData.onItemClick}
          onRefContextMenu={onRefContextMenu}
        />
        {printFormVouPayload &&
          createPortal(
            <PrintTrigger
              config={printFormVouPayload.config}
              records={printFormVouPayload.records}
              onDone={onClosePrintFormVou}
            />,
            document.body
          )}
        {Boolean(tooltipPosition) &&
          createPortal(
            <Tooltip
              lines={tooltipData?.items ?? []}
              loading={tooltipData?.loading}
              metaData={tooltipData?.metaData ?? []}
              position={tooltipPosition as Position}
              width={tooltipData?.width ?? null}
            />,
            document.body
          )}
        {params.value
          ?.slice(0, TIME_LINE_CELL_MAX_ITEMS_COUNT)
          .map((item: ObjectDataRecord, index: number) => {
            const isEventSelected = cellContextRef.current.selectedCells[cellId]?.events.some(
              e => e[objectXPKField?.name] === item[objectXPKField?.name]
            )
            const isItemLoading = loadingItemIds.includes(String(item[objectXPKField?.name]))

            const blockLoadingPointer = (e: React.SyntheticEvent) => {
              e.preventDefault()
              e.stopPropagation()
            }

            return (
              <div
                key={index}
                style={{
                  width: '100%',
                  height: '100%',
                  position: 'relative',
                  ...(isEventSelected && !isCellSelected && SELECTED_CELL_STYLE),
                }}
                onMouseOver={event => handleCellItemMouseOver(event, item, isItemLoading)}
                onClick={event => {
                  if (isItemLoading || cellContextRef.current.isDraggingActive) {
                    event.stopPropagation()

                    return
                  }

                  handleCellItemLeftClick(event, item)
                }}
                onContextMenu={event => {
                  if (isItemLoading) {
                    event.preventDefault()
                    event.stopPropagation()

                    return
                  }

                  handleCellItemRightClick(event, item)
                }}
                onDoubleClick={event => {
                  if (isItemLoading) {
                    event.stopPropagation()

                    return
                  }

                  handleCellItemDoubleLeftClick(event, item)
                }}
                onMouseDown={event => {
                  if (isItemLoading) {
                    event.stopPropagation()

                    return
                  }

                  if (
                    data.objectXActionsObject[EVENT_CODE.ON_DRAG_START]?.actionCode ===
                    ACTION_CODE.DRAG_START
                  ) {
                    cellItemDrag?.onEventMouseDown({
                      e: event,
                      item,
                      rowId: params.row.id as string,
                      columnId: String(params.field),
                      rowData: params.row as ObjectDataRecord,
                      cellEvents: (params.value as ObjectDataRecord[]) || [],
                    })
                  }
                }}
                onMouseLeave={event => {
                  if (isItemLoading) {
                    return
                  }

                  handleCloseTooltip()
                }}
              >
                {objectXCellRenderer({ config, params, cellItem: item, cellItemIndex: index })}
                {isItemLoading && (
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      zIndex: 2,
                      pointerEvents: 'auto',
                      cursor: 'wait',
                    }}
                    onClick={blockLoadingPointer}
                    onContextMenu={blockLoadingPointer}
                    onDoubleClick={blockLoadingPointer}
                    onMouseDown={blockLoadingPointer}
                    onMouseOver={blockLoadingPointer}
                    onMouseUp={blockLoadingPointer}
                    onPointerDown={blockLoadingPointer}
                  >
                    <Skeleton
                      variant='rounded'
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        zIndex: 0,
                        width: '100%',
                        height: '100%',
                        opacity: 0.5,
                        borderRadius: '4px',
                        pointerEvents: 'none',
                      }}
                    />
                  </Box>
                )}
              </div>
            )
          })}
      </Box>
    </>
  )
}
